import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";

const getProxyUrl = () => {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  return `https://${projectId}.supabase.co/functions/v1/decoprof-orders-proxy`;
};

const getHeaders = () => {
  const sessionToken = localStorage.getItem('auth_session_token');
  const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  return {
    'Content-Type': 'application/json',
    'x-session-token': sessionToken || '',
    'apikey': anonKey,
    'Authorization': `Bearer ${anonKey}`,
  };
};

export interface InventoryResult {
  product_id: string;
  product_naam: string;
  eenheid: string;
  locatie: string;
  voorraad: number;
}

export const usePackerCorrection = () => {
  const [searchResults, setSearchResults] = useState<InventoryResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const searchInventory = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        action: 'search_inventory',
        query,
      });
      const res = await fetch(`${getProxyUrl()}?${params}`, {
        headers: getHeaders(),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error('Zoekfout');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Zoekfout');
      setSearchResults(json.data || []);
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        console.error('Search error:', e);
        setSearchResults([]);
      }
    } finally {
      setIsSearching(false);
    }
  }, []);

  const saveCorrection = useCallback(async (
    ordernumber: string,
    lineitemindex: number,
    selected: InventoryResult,
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'save_correction',
          ordernumber,
          lineitemindex,
          selected_product_id: selected.product_id,
          selected_product_naam: selected.product_naam,
          selected_locatie: selected.locatie,
          selected_voorraad: selected.voorraad,
          selected_eenheid: selected.eenheid,
        }),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Opslaan mislukt');
      toast.success('Correctie opgeslagen');
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const saveNoMatch = useCallback(async (
    ordernumber: string,
    lineitemindex: number,
  ): Promise<boolean> => {
    setIsSaving(true);
    try {
      const res = await fetch(getProxyUrl(), {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          action: 'save_correction',
          ordernumber,
          lineitemindex,
          no_match: true,
        }),
      });
      if (!res.ok) throw new Error('Opslaan mislukt');
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Opslaan mislukt');
      toast.success('Gemarkeerd als geen basis nodig');
      return true;
    } catch (e: any) {
      toast.error(e.message);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setSearchResults([]);
    abortRef.current?.abort();
  }, []);

  return {
    searchInventory,
    saveCorrection,
    saveNoMatch,
    searchResults,
    isSearching,
    isSaving,
    clearResults,
  };
};
